// F3/§2.13: `/embed/:token` — a minimal player route with no app shell, meant to sit inside an
// `<iframe>` (Cipher/Slack/Notion/Linear/Jira unfurls, and oEmbed's own `html`). The server's
// `/embed/{token}` route (link_preview.rs) serves this SPA's `index.html` with
// `Content-Security-Policy: frame-ancestors *` for the *initial* document only — this
// component is what actually renders once the bundle boots inside that frame. A gated link
// shows the same ShareGate inline, same as SharePage; the difference from SharePage is layout
// only (fills the frame, no topbar/description/transcript/comments).
import { lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import NotFound from "../pages/NotFound";
import LinkUnavailable from "../pages/LinkUnavailable";
import { PageLoader } from "../components/ui";
import CappedState from "./CappedState";
import ShareGate from "./ShareGate";
import { useShareWatch } from "./useShareWatch";
import { offeredSource } from "./playableSource";
import "./watch.css";
import { Button } from "@grackle/ui";

const Player = lazy(() => import("./Player"));

const SpinIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5" />
  </svg>
);
const AlertIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 8.5v4.5M12 16.5h.01M10.3 4.3 2.8 17.5A2 2 0 0 0 4.5 20.5h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />
  </svg>
);

export default function EmbedPage() {
  const { token } = useParams();
  const { watch, notFound, gone, gate, loadError, unlockToken, reload, onUnlocked } = useShareWatch(token);

  if (notFound) {
    return <NotFound />;
  }
  // SD60: a processing grackle plays from its master too.
  const source = watch ? offeredSource(watch) : null;
  if (gone) {
    return <LinkUnavailable />;
  }

  return (
    <div className="embed-shell">
      {loadError ? (
        <div className="center-page" style={{ padding: 0 }}>
          {loadError}{" "}
          <Button variant="secondary" style={{ marginLeft: 10 }} onClick={reload}>
            Retry
          </Button>
        </div>
      ) : gate && token ? (
        <ShareGate token={token} gate={gate} unlockToken={unlockToken} onUnlocked={onUnlocked} />
      ) : !watch ? (
        <PageLoader />
      ) : watch.capped ? (
        // FR-ST24: the same capped message as the watch page, inside the frame.
        <CappedState title={watch.grackle.title} posterUrl={watch.poster_url} capped={watch.capped} embedded />
      ) : source ? (
        <Suspense fallback={<div className="watch-status-panel" />}>
          <Player
            src={source.src}
            kind={source.kind}
            onSourceExpired={() => void reload()}
            poster={watch.poster_url}
            title={watch.grackle.title}
            recordingId={watch.grackle.id}
          />
        </Suspense>
      ) : watch.grackle.status === "failed" ? (
        <div className="watch-status-panel">
          <div className="watch-status-icon tone-failed">{AlertIcon}</div>
          <div className="watch-status-title">Processing failed</div>
        </div>
      ) : (
        <div className="watch-status-panel">
          <div className="watch-status-icon tone-processing">{SpinIcon}</div>
          <div className="watch-status-title">
            {watch.grackle.status === "uploading" ? "Still uploading" : "Still processing"}
          </div>
        </div>
      )}
    </div>
  );
}
